import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { QuotationsService } from '../../../features/quotations/quotations.service';

import { EFavorite, EFavoriteType } from '../../enums/favorite.enum';
import { IQuotation } from '../../interfaces/quotation.interface';
import { BaseResponse } from '../../models/base-response.model';
import {
  ClearQuotation,
  LoadQuotation,
  MarkAsFavorite,
  SmartBanner,
  UpdateCurrentQuotation,
} from './quotation.actions';
import { QuotationStateModel } from './quotation.models';

const QUOTATION_STATE_TOKEN = new StateToken<QuotationStateModel>('quotation');

const defaultQuotationState = {
  quotation: null,
} as QuotationStateModel;

@State<QuotationStateModel>({
  name: QUOTATION_STATE_TOKEN,
  defaults: defaultQuotationState,
})
@Injectable()
export class QuotationState {
  private quotationsService = inject(QuotationsService);

  @Selector()
  static currentQuotation(state: QuotationStateModel): IQuotation | null {
    return state.quotation;
  }

  @Selector()
  static currentQuotationId(state: QuotationStateModel): string | null {
    return state.quotation?.id || null;
  }

  @Action(LoadQuotation)
  loadQuotation(
    ctx: StateContext<QuotationStateModel>,
    action: LoadQuotation,
  ): Observable<BaseResponse<IQuotation> | null> {
    const currentState = ctx.getState();

    // Early return if quotation is already cached and cache is enabled
    if (currentState.quotation && currentState.quotation.id === action.id && action.isCacheEnabled) {
      return of(null);
    }

    // Execute API call
    return this.quotationsService.queryQuotation(action.id).pipe(
      tap((response: BaseResponse<IQuotation>) => {
        if (response.isSuccess && response.result) {
          ctx.patchState({
            quotation: response.result,
          });
        }
      }),
      catchError((error) => {
        console.error('Error loading quotation:', error);
        // Re-throw error so ofActionErrored triggers if needed
        return throwError(() => error);
      }),
    );
  }

  @Action(ClearQuotation)
  async clearQuotation(ctx: StateContext<QuotationStateModel>) {
    ctx.setState({
      quotation: null,
    });
  }

  @Action(UpdateCurrentQuotation)
  updateCurrentQuotation(ctx: StateContext<QuotationStateModel>, action: UpdateCurrentQuotation) {
    ctx.patchState({
      quotation: action.quotation,
    });
  }

  @Action(MarkAsFavorite)
  markAsFavorite(ctx: StateContext<QuotationStateModel>, action: MarkAsFavorite) {
    const currentState = ctx.getState();

    // Guard: Only proceed if we have a quotation and it matches the action ID
    if (!currentState.quotation || currentState.quotation.id !== action.quotationId) {
      return;
    }

    // Toggle favorite status
    const newFavoriteStatus = currentState.quotation.favorite === EFavorite.no ? EFavorite.yes : EFavorite.no;

    // Update state immediately
    ctx.patchState({
      quotation: {
        ...currentState.quotation,
        favorite: newFavoriteStatus as EFavoriteType,
      },
    });
  }

  @Action(SmartBanner)
  smartBanner(ctx: StateContext<QuotationStateModel>, action: SmartBanner) {
    const currentState = ctx.getState();

    // Guard: Only proceed if we have a quotation and it matches the action ID
    if (!currentState.quotation || currentState.quotation.id !== action.quotationId) {
      return;
    }

    // Toggle smart banner status
    const newShowSmartBanner = !currentState.quotation.showSmartBanner;

    // Update state immediately
    ctx.patchState({
      quotation: {
        ...currentState.quotation,
        showSmartBanner: newShowSmartBanner,
      },
    });
  }
}
